/**
 * The marketing assistant — design/PROTOTYPE-SPEC.md §13, docs/AI-ASSISTANT.md.
 *
 * Two buttons, one generation each: ten social post ideas, or one marketing
 * email. The copy is written from the clinic's name, its live promotions and
 * the retail lines that are not selling.
 *
 * ## Why it lives on Promotions and not inside Targets
 *
 * The prototype puts it on Targets' Plan-ahead tab, which is Premium. Its best
 * input is live promotions, which are Pro — so there it would be gated by the
 * screen it happened to sit on, and a Pro clinic would have the data and no way
 * to use it. Here it sits under the promotions it writes about.
 *
 * ## It hides itself when it cannot run
 *
 * `available: false` means no API key is configured. The card renders nothing
 * at all in that case: a feature that cannot work should not be advertised
 * inside the product, and "coming soon" in a paid plan reads as a broken promise.
 *
 * ## The button is disabled while generating — that is politeness
 *
 * The real limits (one every ten seconds, a monthly allowance) are enforced by
 * the API. This only stops an honest double-click.
 */
import { useEffect, useState } from 'react';
import {
  ApiError,
  generateMarketing,
  getAiAssistant,
  type AiAssistantStatus,
  type AiGeneration,
  type AiKind,
} from '../lib/api.js';
import { c, mono, sans } from '../theme.js';
import { formatDate } from './format.js';

const KINDS: { kind: AiKind; label: string; working: string }[] = [
  { kind: 'social', label: '10 social post ideas', working: 'Writing ideas…' },
  { kind: 'email', label: 'A marketing email', working: 'Writing the email…' },
];

const KIND_TITLE: Record<AiKind, string> = {
  social: 'Social post ideas',
  email: 'Marketing email',
};

export function MarketingAssistant({ clinicName }: { clinicName: string }) {
  const [status, setStatus] = useState<AiAssistantStatus | null>(null);
  const [busy, setBusy] = useState<AiKind | null>(null);
  const [shown, setShown] = useState<AiGeneration | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getAiAssistant()
      .then((s) => {
        if (cancelled) return;
        setStatus(s);
        // Show the last one written, so a refresh does not lose paid-for copy.
        setShown(s.recent[0] ?? null);
      })
      // Any failure here — including the plan gate — means: show nothing.
      .catch(() => {
        if (!cancelled) setStatus(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!status || !status.available) return null;

  const { allowance } = status;
  const exhausted = allowance.remaining <= 0;

  async function run(kind: AiKind) {
    if (busy) return;
    setBusy(kind);
    setError(null);
    setCopied(false);
    try {
      const res = await generateMarketing(kind);
      setShown(res.generation);
      setStatus((s) => (s ? { ...s, allowance: res.allowance } : s));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reach the assistant. Try again.');
    } finally {
      setBusy(null);
    }
  }

  async function copy() {
    if (!shown) return;
    try {
      await navigator.clipboard.writeText(shown.text);
      setCopied(true);
    } catch {
      setError('Copying is not available in this browser. Select the text instead.');
    }
  }

  const disabled = busy !== null || exhausted || !status.mayGenerate;

  return (
    <section
      aria-label="Marketing assistant"
      style={{
        marginTop: 16,
        borderRadius: 18,
        padding: '24px 26px',
        color: c.onDarkBright,
        fontFamily: sans,
        // The prototype had a purple gradient here (PROTOTYPE-SPEC §13). Purple is
        // gone from the product; this is the same dark green as the figure cards.
        background: c.greenDeep,
        border: '1px solid #4A3A66',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <h2 style={{ fontFamily: sans, letterSpacing: '-0.03em', fontWeight: 300, fontSize: 22, margin: 0 }}>
          Marketing assistant
        </h2>
        <span style={{ fontSize: 12.5, color: '#CFE0D4' }}>
          {allowance.remaining} of {allowance.limit} left this month · resets{' '}
          {formatDate(allowance.resetsOn)}
        </span>
      </div>

      <p style={{ fontSize: 13.5, color: c.onDarkMuted, margin: '8px 0 16px', lineHeight: 1.7 }}>
        Written for {clinicName} from your live promotions and the retail lines that are not
        selling. Nothing about any client or patient is used. We do not hold any.
      </p>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {KINDS.map((k) => (
          <button
            key={k.kind}
            type="button"
            disabled={disabled}
            onClick={() => void run(k.kind)}
            style={{
              height: 42,
              padding: '0 18px',
              borderRadius: 980,
              border: 'none',
              background: '#CFE0D4',
              color: c.ink,
              fontFamily: sans,
              fontSize: 14,
              fontWeight: 500,
              cursor: disabled ? 'default' : 'pointer',
              opacity: disabled ? 0.5 : 1,
            }}
          >
            {busy === k.kind ? k.working : k.label}
          </button>
        ))}
      </div>

      {!status.mayGenerate && (
        <p style={{ fontSize: 12.5, color: c.onDarkMuted, margin: '12px 0 0' }}>
          An owner or manager can generate copy.
        </p>
      )}
      {exhausted && status.mayGenerate && (
        <p style={{ fontSize: 12.5, color: '#CFE0D4', margin: '12px 0 0' }}>
          You have used this month’s {allowance.limit}. They reset on{' '}
          {formatDate(allowance.resetsOn)}.
        </p>
      )}

      {error && (
        <div
          role="alert"
          style={{
            marginTop: 14,
            background: 'rgba(196,103,63,0.2)',
            border: '1px solid rgba(196,103,63,0.5)',
            borderRadius: 12,
            padding: '10px 13px',
            fontSize: 13,
            lineHeight: 1.6,
          }}
        >
          {error}
        </div>
      )}

      {shown && (
        <div
          style={{
            marginTop: 18,
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 12,
            padding: '16px 18px',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
              marginBottom: 10,
            }}
          >
            <span
              style={{
                fontSize: 11,
                textTransform: 'lowercase', fontFamily: mono,
                color: '#CFE0D4',
                fontWeight: 400,
              }}
            >
              {KIND_TITLE[shown.kind]}
            </span>
            <button
              type="button"
              onClick={() => void copy()}
              style={{
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.3)',
                color: c.onDarkBright,
                borderRadius: 980,
                padding: '6px 12px',
                fontSize: 12.5,
                fontWeight: 500,
                fontFamily: sans,
                cursor: 'pointer',
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          {/* pre-wrap, and rendered as TEXT: model output is never treated as HTML. */}
          <div style={{ fontSize: 13.5, lineHeight: 1.75, whiteSpace: 'pre-wrap' }}>
            {shown.text}
          </div>
          <p style={{ fontSize: 11.5, color: c.onDarkFaint, margin: '12px 0 0' }}>
            Read it before you send it. It is a draft written by an AI model, and it can be wrong
            about your clinic.
          </p>
        </div>
      )}
    </section>
  );
}
